import { useContextError } from "common-frontend-components";
import { QuestionContext } from "./QuestionContext";
import { EditQuestionFormContext } from "./EditQuestionFormContext";
import { useTranslation } from "react-i18next";
import { useUpdateQuestion } from "@pulse/hooks/surveyHooks";
import { useSurveySavedNotification } from "../../hooks";
import { useEditSurveyForm } from "../formHooks";
import type { CreateQuestion } from "@pulse/api/surveyApi";
import { validateQuestionFields } from "./questionValidation";

function useQuestion() {
    return useContextError(QuestionContext.name, QuestionContext);
}

function useEditQuestionForm() {
    return useContextError(EditQuestionFormContext.name, EditQuestionFormContext);
}

function useEditQuestionFormGenerator() {
    const { question } = useQuestion();
    const { t } = useTranslation();
    const mutation = useUpdateQuestion(question.id);
    const surveySavedNotification = useSurveySavedNotification();
    const form = useEditSurveyForm({
        defaultValues: {
            questionText: question.questionText,
            questionOptions: question.questionOptions,
            allowComments: question.allowComments,
            questionType: question.questionType,
            questionOrder: question.questionOrder,
            driverId: question.driverId,
            optional: question.optional,
        } as CreateQuestion,
        validators: {
            onChange: ({ value }) => validateQuestionFields(value, t),
        },
        onSubmit: async ({ value, formApi }) => {
            if (formApi.state.isDefaultValue) {
                return;
            }
            if (Array.isArray(value.questionOptions))
                value.questionOptions = value.questionOptions.filter((o) => o.trim() !== "");
            const questionData: Partial<CreateQuestion> = { ...value };
            delete questionData.questionOrder;
            const promise = mutation.mutateAsync(questionData);

            surveySavedNotification(promise);
        },
    });

    return form;
}

export { useQuestion, useEditQuestionForm, useEditQuestionFormGenerator };
